/**
 * 🧭 왼쪽 메뉴 「내 배치」 (v2.320.0 · 사용자 11-02 「메뉴가 많아져 한 화면에 다 안 나오고, 직원마다 쓰는 메뉴가 다르다 —
 *   쓰는 메뉴만 자주 쓰는 순서대로, 안 쓰는 메뉴는 숨김으로 묶어 펼치면 나오게 · 끌어다 놓기로」).
 *
 *   - 저장: app_user.preferences.navLayout = { shown: [경로…], hidden: [경로…] } — 서버(/auth/preferences)에 사람마다.
 *     어느 PC·폰에서 들어와도 같은 배치다(비상벨·영업진행 표 칸 설정과 같은 자리).
 *   - 없으면(기본): 종전 8묶음 그대로(lib/menu.ts MENU_GROUPS · 도구·관리는 접힘).
 *   - 있으면: 보이는 메뉴를 **묶음 머리글 없이 적은 순서대로** 그리고, 숨긴 메뉴는 맨 아래 「숨긴 메뉴」 한 묶음으로
 *     (기본 접힘 · 머리글을 누르면 펼침 · 켜진 메뉴가 안에 있으면 저절로 펼침 — 도구·관리 묶음과 같은 동작).
 *   - **권한이 먼저다** — 배치에 적혀 있어도 권한 없는 메뉴는 안 보인다(visibleItems · 화면 숨김 ≠ 서버 차단은 종전과 같다).
 *   - 배치에 없는 메뉴(그 뒤에 생긴 새 메뉴·권한이 새로 열린 메뉴)는 **보이는 쪽 맨 뒤**에 붙는다 — 새 기능이 조용히 숨지 않게.
 *   - 대시보드(`/`)는 숨길 수 없다 — 로그인 직후 자리이자 어디서든 돌아갈 곳(숨김에 적혀 있어도 보이는 쪽으로).
 *   - 깜빡임 막기: 서버 응답 전 첫 그림을 위해 마지막 배치를 이 브라우저(localStorage · 사람별 열쇠)에도 적어 둔다 — 정답은 늘 서버 값.
 *
 *   검사: scripts/check-menu.ts ⑪ 이 이 함수를 직접 부른다(권한·새 메뉴·홈·중복·없는 경로).
 */
import { MENU_GROUPS, visibleItems, type MenuItem } from './menu';
import type { AuthUser, NavLayout } from './types';

/** 숨길 수 없는 메뉴 — 대시보드 */
export const HOME_PATH = '/';

/** 사이드바 「숨긴 메뉴」 묶음의 열쇠 — 펼침 기억(localStorage · lib/nav-groups-open.ts)에 묶음 머리글 대신 쓴다 */
export const HIDDEN_GROUP_KEY = '__hidden__';

export type ResolvedNav = {
  /** 내 배치가 있나(false 면 기본 8묶음으로 그린다) */
  custom: boolean;
  /** 보이는 메뉴 — 이 순서대로 */
  shown: MenuItem[];
  /** 숨긴 메뉴 — 「숨긴 메뉴」 묶음 안 */
  hidden: MenuItem[];
};

/** 권한을 거른 전체 메뉴 — 기본 순서(묶음 순 · 묶음 안 순) */
export function allMenuItems(user: AuthUser | null): MenuItem[] {
  return MENU_GROUPS.flatMap((g) => visibleItems(g.items, user));
}

/** 메뉴가 속한 기본 묶음 이름 — 편집 창에서 작게 보여 준다(「재고·매입」) */
export function groupHeadingOf(path: string): string {
  return MENU_GROUPS.find((g) => g.items.some((it) => it.path === path))?.heading ?? '';
}

/** 저장된 값이 배치 모양인가 — 옛 판·손으로 고친 값은 기본 배치로 본다 */
export function isNavLayout(x: unknown): x is NavLayout {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  const strs = (a: unknown): a is string[] => Array.isArray(a) && a.every((s) => typeof s === 'string');
  return strs(o.shown) && strs(o.hidden);
}

/**
 * 저장된 배치 + 권한 → 실제로 그릴 두 목록.
 *   보이는 쪽을 먼저 집고(중복·권한 없음·없어진 경로는 건너뜀), 숨긴 쪽은 그 다음(보이는 쪽에 이미 있으면 보이는 쪽이 이긴다 · 홈은 숨기지 않는다),
 *   어느 쪽에도 없는 메뉴는 보이는 쪽 맨 뒤.
 */
export function resolveNavLayout(user: AuthUser | null, layout: NavLayout | null | undefined): ResolvedNav {
  const all = allMenuItems(user);
  if (!isNavLayout(layout)) return { custom: false, shown: all, hidden: [] };
  const byPath = new Map(all.map((it) => [it.path, it] as const));
  const used = new Set<string>();
  const pick = (paths: string[]): MenuItem[] => {
    const out: MenuItem[] = [];
    for (const p of paths) {
      const it = byPath.get(p);
      if (!it || used.has(p)) continue;
      used.add(p);
      out.push(it);
    }
    return out;
  };
  const shown = pick(layout.shown);
  const hidden = pick(layout.hidden.filter((p) => p !== HOME_PATH));
  const rest = all.filter((it) => !used.has(it.path));
  return { custom: true, shown: [...shown, ...rest], hidden };
}

/** 편집 창의 두 목록(경로) → 저장 모양. 홈이 숨김에 있으면 보이는 쪽 맨 앞으로 */
export function toNavLayout(shown: string[], hidden: string[]): NavLayout {
  const h = hidden.filter((p) => p !== HOME_PATH);
  const s = hidden.includes(HOME_PATH) && !shown.includes(HOME_PATH) ? [HOME_PATH, ...shown] : [...shown];
  return { shown: s, hidden: h };
}

/* ── 이 브라우저에 적어 두는 마지막 배치(깜빡임 방지 · 정답은 서버) ── */
const cacheKey = (userId: string): string => `sm-nav-layout:${userId}`;

export function readCachedNavLayout(userId: string): NavLayout | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    const v: unknown = raw ? JSON.parse(raw) : null;
    return isNavLayout(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeCachedNavLayout(userId: string, layout: NavLayout | null): void {
  try {
    if (layout) localStorage.setItem(cacheKey(userId), JSON.stringify(layout));
    else localStorage.removeItem(cacheKey(userId));
  } catch {
    /* localStorage 비활성 환경 — 화면은 서버 값으로 동작 */
  }
}
